// A deliberate fork of the live ConsultChatPanel: threading send/attach/disabled props into a read-only view is how one acquires a write path.

import React from "react";
import { PaperClipOutlined } from "@ant-design/icons";

import { ConsultChatMessage } from "../../types/appointments/ConsultChatTypes";
import { formatLocalDateTime } from "../../utils/userPrefs/DateTimeUtils";
import { formatFileSize } from "../../utils/fileUtils";

interface ArchivedChatViewProps {
	messages: ConsultChatMessage[];
}

const ROLE_LABELS: Record<ConsultChatMessage["senderRole"], string> = {
	practitioner: "Practitioner",
	patient: "Patient",
};

// A patient's senderName is null by design — talk refuses to take it from their payload.
const senderDisplayName = (message: ConsultChatMessage): string => message.senderName?.trim() || ROLE_LABELS[message.senderRole];

const ArchivedChatView: React.FC<ArchivedChatViewProps> = ({ messages }) => {
	if (!messages.length) return null;

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="appointments-consult-chat-log">
			{messages.map((message) => (
				<div
					key={message.id}
					data-testid="appointments-consult-chat-message"
					data-role={message.senderRole}
					style={{
						border: "1px solid var(--mg-border)",
						borderRadius: 8,
						padding: "8px 12px",
						background: message.senderRole === "practitioner" ? "var(--mg-bg-subtle)" : "var(--mg-bg-card)",
					}}
				>
					<div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap", marginBottom: 4 }}>
						<span style={{ fontSize: 12, fontWeight: 500 }}>{senderDisplayName(message)}</span>
						<span style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>{ROLE_LABELS[message.senderRole]}</span>
						<span style={{ fontSize: 11, color: "var(--mg-text-secondary)", marginLeft: "auto" }}>{formatLocalDateTime(new Date(message.timestamp))}</span>
					</div>

					{/* Participant-authored text, so never dangerouslySetInnerHTML — unlike the report body above it. */}
					{message.content && <div style={{ fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{message.content}</div>}

					{message.attachments.length > 0 && (
						<div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: message.content ? 6 : 0 }}>
							{message.attachments.map((attachment, index) => (
								<div key={attachment.id || `${message.id}-${index}`} style={{ fontSize: 12 }} data-testid="appointments-consult-chat-attachment">
									<PaperClipOutlined style={{ marginRight: 6, color: "var(--mg-text-secondary)" }} />
									<span style={{ wordBreak: "break-word" }}>{attachment.name}</span>
									{attachment.size > 0 && <span style={{ color: "var(--mg-text-secondary)" }}> ({formatFileSize(attachment.size)})</span>}
								</div>
							))}
							{/* No download link in v1: the file rests in the patient's records and no path is wired here. */}
							<div style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>Saved to the patient's records.</div>
						</div>
					)}
				</div>
			))}
		</div>
	);
};

export default ArchivedChatView;
